import React, {
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import usePlacesAutocomplete, { Suggestion } from 'use-places-autocomplete'
import Spinner from 'common/Spinner'
import _isEmpty from 'lodash/isEmpty'
import GoogleMapsContext from 'context/common/GoogleMapsContext'
import styles from './index.module.scss'
import AutoCompleteSuggestions from './AutoCompleteSuggestions'
import classNames from 'classnames'
import Icon from 'common/Icon'
import { SLATE_GRAY } from 'constants/colors'
import { getFirstLocationResultData } from 'utils/common'
import { usedForFeature } from 'constants/logEvents'

type LocationAutoCompleteProps = {
  showClearOption?: boolean
  onSelect: (result: GoogleLocationResult) => void
  prefillInputValue?: string
  // For customizations
  containerClassName?: string
  inputClassName?: string
  suggestionContainerClassName?: string
  suggestionItemClassName?: string
}

const LocationAutoComplete: FC<LocationAutoCompleteProps> = ({
  onSelect,
  showClearOption = false,
  prefillInputValue = '',
  containerClassName = '',
  inputClassName = '',
  suggestionContainerClassName = '',
  suggestionItemClassName = '',
}) => {
  const searchOptions = { types: ['(regions)'] }
  const locationInputRef = useRef(null)
  const [inputValue, setInputValue] = useState(prefillInputValue)
  const { googleMapsScriptLoading } = useContext(GoogleMapsContext)
  const {
    init,
    suggestions,
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      ...searchOptions,
    },
    debounce: 300,
    initOnMount: false,
    callbackName: 'initMap',
  })

  useEffect(() => {
    if (locationInputRef.current) {
      locationInputRef.current.focus()
    }
  }, [])
  useEffect(() => {
    if (prefillInputValue) {
      setInputValue(prefillInputValue)
    }
  }, [prefillInputValue])

  useMemo(() => {
    // scritly checking for only boolean false value
    if (googleMapsScriptLoading === false) {
      init()
    }
  }, [googleMapsScriptLoading])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setValue(value)
    setInputValue(value)
  }

  const handleSelect = async ({
    place_id,
    structured_formatting,
  }: Suggestion) => {
    try {
      const { firstResult, latLong } = await getFirstLocationResultData(
        place_id,
        usedForFeature.SEFriendlyHeaderFilterLocationDropdown
      )
      onSelect({
        ...firstResult,
        ...latLong,
        ...structured_formatting,
      })
      clearSuggestions()
    } catch (error) {
      //error handling not required
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !_isEmpty(suggestions.data)) {
      handleSelect(suggestions.data[0])
    }
  }

  return (
    <div className={classNames('position-relative d-flex', containerClassName)}>
      <input
        ref={locationInputRef}
        autoComplete="off"
        disabled={googleMapsScriptLoading}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        value={inputValue}
        className={classNames(styles.input, inputClassName)}
      />
      {suggestions.loading && (
        <div className="align-self-center ml-auto mr-1">
          <Spinner className="mr-2" size="sm" />
        </div>
      )}
      {!suggestions.loading && !!inputValue && showClearOption && (
        <button
          type="button"
          aria-label="Clear location"
          className={classNames(
            styles.close_button,
            'align-self-center ml-auto mr-1 flex-center p-0 ~border-0'
          )}
          onClick={() => setInputValue('')}
        >
          <Icon icon="close" className="fa-md" fill={SLATE_GRAY} />
        </button>
      )}
      <AutoCompleteSuggestions
        onSelect={handleSelect}
        suggestions={suggestions}
        suggestionItemClassName={suggestionItemClassName}
        suggestionContainerClassName={suggestionContainerClassName}
      />
    </div>
  )
}

export default LocationAutoComplete
